import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { LockKey, ShieldCheck, UserPlus } from "@phosphor-icons/react";

const LOGO = "/logo-servitech.png";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwbWFjaGluZXJ5JTIwZmFjdG9yeSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg2MTg4OTAzfDA&ixlib=rb-4.1.0&q=85";

function errMsg(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return fallback;
}

export default function LoginPage() {
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const data = await login(email.trim(), password);
        navigate(data.role === "client" ? "/portal" : "/admin");
      } else {
        await register(email.trim(), password);
        navigate("/portal");
      }
    } catch (err) {
      setError(errMsg(err.response?.data?.detail, mode === "login" ? t.loginError : t.registerError));
    } finally {
      setLoading(false);
    }
  };

  const tabCls = (m) =>
    `flex-1 pb-2 text-xs font-bold uppercase tracking-wider transition-colors duration-200 border-b-2 ${mode === m ? "border-primary text-white" : "border-white/15 text-white/50 hover:text-white/80"}`;

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A]">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${LOGIN_BG})` }} />
      <div className="absolute inset-0 bg-black/75" />

      <div className="absolute top-6 right-6 z-20"><LanguageToggle /></div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <button onClick={() => navigate("/")} className="mb-8 flex items-center gap-4">
            <div className="rounded bg-white px-4 py-3 shadow-sm">
              <img src={LOGO} alt="Servitech Xpert" className="h-10 w-auto object-contain" />
            </div>
            <div className="leading-none text-left">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60">{t.portalName}</div>
            </div>
          </button>

          <div className="border border-white/15 bg-[#121212]/90 backdrop-blur-xl p-8">
            <div className="mb-6 flex gap-4">
              <button data-testid="tab-login" onClick={() => { setMode("login"); setError(""); }} className={tabCls("login")}>{t.signIn}</button>
              <button data-testid="tab-register" onClick={() => { setMode("register"); setError(""); }} className={tabCls("register")}>{t.createClientAccount}</button>
            </div>

            <h1 className="font-display text-3xl font-black tracking-tighter text-white">
              {mode === "login" ? t.loginTitle : t.createClientAccount}
            </h1>
            <p className="mt-2 text-sm text-white/60">{mode === "login" ? t.loginSubtitle : t.clientRegisterInfo}</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/70 mb-2">{t.email}</label>
                <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-white/20 bg-black/40 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-primary focus:ring-2 focus:ring-primary transition-colors duration-200"
                  placeholder="nom@courriel.com" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/70 mb-2">{t.password}</label>
                <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-white/20 bg-black/40 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-primary focus:ring-2 focus:ring-primary transition-colors duration-200"
                  placeholder="••••••••" />
              </div>

              {error && (
                <div data-testid="login-error" className="border border-primary/50 bg-primary/10 px-4 py-3 text-sm text-primary">{error}</div>
              )}

              <button data-testid="login-submit-button" type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 bg-primary px-4 py-3 text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0">
                {mode === "login" ? <LockKey size={18} weight="bold" /> : <UserPlus size={18} weight="bold" />}
                {loading ? t.signingIn : mode === "login" ? t.signIn : t.createAccount}
              </button>
            </form>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.15em] text-white/40">
            <ShieldCheck size={14} weight="bold" />
            {t.confidential}
          </div>
        </div>
      </div>
    </div>
  );
}
